import {
    LOCATION_CHANGE, RouterAction, RouterState, Store,
    applyMiddleware, createStore, routerMiddleware, routerReducer, syncHistoryWithStore, thunk
} from 'typed/utils/redux';
import { History } from 'history';
import { history } from 'routes';
import { RequestStatus, defaults, entities } from 'typed/api';
import { AppAction, Action as TypedAction, UntypedAction } from 'typed/actions';
import { ExtractType, IAction } from 'typed/actions/utils';
import { IReducer, combineReducers, entityLoadAllReducer, networkActionReducer, switchReducers } from 'typed/reducers/utils';
import typedReducers from 'typed/reducers';
import * as untypedReducers from 'reducers';
import * as middlewares from 'middlewares';
import { ActionType, SESSION_EXPIRATION_TIMEOUT } from 'utils/constants';
import { assertNever, deepAssign, deepFreeze } from 'typed/utils';

// TODO fix types when we don't have untyped actions and reducers
// currently types are what they should be, not what they actually are according to implementation

type EveryAction = RouterAction | TypedAction | IAction<'@@redux/INIT' | 'UNTYPED_ACTION' | 'UNTYPED_ACTION_PENDING' | 'UNTYPED_ACTION_SUCCESS' | 'UNTYPED_ACTION_FAIL', undefined>;


const
    ur = (untypedReducers.default || untypedReducers) as { [k: string]: IReducer<any, UntypedAction>; },
    mixedReducers = { ...typedReducers };

for (const i in (mixedReducers as any)) {
    if (Object.prototype.hasOwnProperty.call(mixedReducers, i)) {
        (mixedReducers as any)[i] = (ur as any)[i] ? switchReducers(undefined as any, (ur as any)[i], (typedReducers as any)[i]) as any : (typedReducers as any)[i];
    }
}

const typedReducer = combineReducers({ ...typedReducers });
type HandledAction = Exclude<AppAction/* handled with untyped reducer, but action had to be typed*/ | Parameters<typeof typedReducer>[1], UntypedAction>;

if (false) { // compile-time checks
    assertNever(undefined as unknown as Exclude<ExtractType<HandledAction>, ExtractType<TypedAction>>, 'Some action creators are missing.');
    assertNever(undefined as unknown as Exclude<ExtractType<TypedAction>, ExtractType<HandledAction>>, 'Some reducers are missing.');
    assertNever(undefined as unknown as Exclude<HandledAction, TypedAction>, 'Action and reducer payload mismatch.');
}

export type State = ReturnType<typeof typedReducer> & {
    [k: string]: unknown;
    routing: RouterState;
    status: RequestStatus;
    app: {
        session: entities.Session;
        [k: string]: unknown;
    };
};

const
    // lazy ass version of soft application reload and data expiration:
    // every network object has `.Custom.expires` field - it's set on every synchronization action (load, edit etc) to some time in future
    // when `location.path` is changed (`location.query` change usually happen on a same page for filtering, settings etc, so it's ignored)
    // or reload is dispatched explicitly with VOID_ALL_DATA
    // this reducer either sets the whole store to `undefined` (if VOID_ALL_DATA or some `.changes` are unhandled)
    // or scans the whole store for expired `.Custom.expires` fields and sets only those objects to`undefined`
    // causing the store to be initialized as in normal Redux flow
    // possible problems:
    // 1. Some parts of store are used during app initialization and currently can not be reloaded without app restart - translations, session, company settings, permissions etc.
    //    Most of them are in `store.app` part, for now it's hardcoded to never reload.
    // 2. Some parts are used with components which are always visible and should be reloaded without dropping existing data to avoid useless spinners - global search, tasks, notifications etc.
    //    Can be solved setting all .Custom.status in a store to UNSYNCED instead of full store reinitialization, but full scan and update might be quite slow and might break some reducers' logic.
    // 3. Plenty of store parts are system enumerations which should not change during application lifecycle. They can be refreshed only when "app update" message is shown.
    // 4. Some parts are not initialized correctly with `undefined` state and cause errors. I fixed some of them but surely there are more.
    // 5. Full scan is a bit slow, and will be slower as the application grows.

    // features to think about:
    // 1. Some persistency flag to solve the first three and a half problems:
    //      An attribute for a store part? Where and how?
    //      An `in use` lock for parts which are currently used by visible components? Needs some huge changes in components and complex logic when many components mount and unmount.
    //      Some `Custom.initial` state instead of `undefined`? When is a object initialized?
    //      Custom expiration logic in reducers for persistent parts? Simple, but a lot of places to change.
    // 2. Randomization of expiration time. Currently application loads a huge bunch of data on initialization or transition to a yet unused page.
    //    All of those objects will be set to expire at about the same time which at some point cause huge reload similar to full application restart.
    //    A simple random offset for every item might make them expire and reload one by one which should be less noticeable.
    //    Though what about parts dependent on other parts?
    // 3. Dump and fetch the whole store to local storage or any other offline place? This would speed up application restart, but might cause troubles if errors happen.
    //    With persistency flag we could only restore system enumerations etc, and only if app version has not changed.

    initialReducer: IReducer<State, EveryAction> = (state, action) => state || mainReducer(state, action),
    networkTrackingReducer: IReducer<State, EveryAction> = (state: State | undefined = undefined as unknown as State, action: EveryAction) =>
        // a very dirty hack
        // while we are loading something, we can not safely reset everything - in many cases the app requests partial data which has to be merged with existing data
        // and if it's dropped during request, the merge fails
        // here we track all actions which contain PENDING, SUCCESS and FAIL words in action type, but obviously it's not enough
        !state.status ? deepAssign(state, { status: RequestStatus.UNSYNCED }) :
        action.type.includes('PENDING') || action.payload && (action as any).payload.status === RequestStatus.PENDING ? deepAssign(state, { status: RequestStatus.PENDING }) :
        action.type.includes('SUCCESS') || action.payload && (action as any).payload.status === RequestStatus.SUCCEEDED ? deepAssign(state, { status: RequestStatus.SUCCEEDED }) :
        action.type.includes('FAIL') || action.payload && (action as any).payload.status === RequestStatus.FAILED ? deepAssign(state, { status: RequestStatus.FAILED }) :
        state,
    /* eslint-disable-next-line */
    deepExpire = <S>(state: S): S => {
        if (state) {
            switch (true) {
                case typeof state !== 'object':
                case state instanceof Date:
                default:
                    return state;

                case (state as any).Custom && (state as any).Custom.expires && (state as any).Custom.expires <= new Date():
                    return undefined as unknown as S;

                case Array.isArray(state):
                    let newArrayState: S | null = null;
                    for (let i = 0; i < (state as any).length; i++) {
                        if ((state as any)[i] as any) {
                            const val = deepExpire((state as any)[i]);
                            if (!newArrayState && val !== (state as any)[i]) {
                                newArrayState = [...(state as any)] as unknown as S;
                            }
                            if (newArrayState) {
                                (newArrayState as any)[i] = val as any;
                            }
                        }
                    }
                    return newArrayState ? deepAssign<any>(state, (newArrayState as any).filter((v: any) => v !== undefined)) : state;

                case !Array.isArray(state):
                    let newState: S | null = null;
                    for (const i in state) {
                        if (Object.prototype.hasOwnProperty.call(state, i)) {
                            const val = deepExpire(state[i]);
                            if (!newState && val !== state[i]) {
                                newState = { ...state };
                            }
                            if (newState) {
                                (newState as any)[i] = val;
                            }
                        }
                    }
                    return newState ? deepAssign(state, newState) : state;
            }
        }
        return state;
    },
    isSafeTransition = (state: RouterState, action: RouterAction): boolean =>
        state?.locationBeforeTransitions && action.payload && !((action.payload as any).state && (action.payload as any).state.internal) &&
        state.locationBeforeTransitions.pathname !== (action.payload as any).pathname,
    expirationReducer: IReducer<State, EveryAction> = (state: State | undefined = undefined as unknown as State, action: EveryAction) => {
        const
            reloadable = state.status === RequestStatus.SUCCEEDED || state.status === RequestStatus.FAILED,
            voidState = action.type === ActionType.VOID_ALL_DATA,
            voidStateImmediately = (action.type === 'SET_WORKSHOP_LANGUAGE_CODE' || action.type === 'CLEAR_WORKSHOP_LANGUAGE_CODE') &&
                action.payload.status === RequestStatus.SUCCEEDED,
            locationChanged = action.type === LOCATION_CHANGE && isSafeTransition(state.routing, action),
            preventExpiration = !!(action.payload as unknown as any)?.pathname?.startsWith('/workshops/') || !!(action.payload as unknown as any)?.pathname?.startsWith('/public/');

        if (reloadable && (voidState || voidStateImmediately || locationChanged && !preventExpiration)) {
            const
                expirationChanges = (state.app as any).changes.length || voidStateImmediately ?
                    initialReducer(undefined, { type: '@@redux/INIT', payload: undefined }) :
                    deepExpire(state),
                appChanges = !voidStateImmediately ?
                    { ...state.app, changes: [] } :
                    {};

            return deepAssign(state, {
                ...expirationChanges,
                ...appChanges,
                routing: state.routing
            });
        }

        return state;
    },
    scrollingReducer: IReducer<State, EveryAction> = (state: State | undefined = undefined as unknown as State, action: EveryAction) =>
        // when we remove a lot of content from the bottom of a scrollable page so scrollbars disappear, and then add some content so they appear again
        // Chrome tries to restore previous scrolling position rather than to preserve current one
        // which I guess is good for e. g. endlessly scrollable pages or some other dynamically loading content, but doesn't work with SPAs:
        // normally when user goes from one page to another Chrome resets scrolling, but with SPA it can't really differentiate page transitions
        // and also it happens without "onscroll" event, which really screws up some absolutely positioned components
        // so we have to do it manually
        // it shouldn't be a reducer, but I'm to lazy to create a middleware
        action.type === LOCATION_CHANGE && isSafeTransition(state.routing, action) ?
            (window.scrollTo(0, 0), state) :
            state!,
    mainReducer: IReducer<State, EveryAction> = combineReducers({
        ...((untypedReducers.default || untypedReducers) as { [k: string]: IReducer<any, UntypedAction>; }),
        ...mixedReducers,
        routing: routerReducer,
        status: (state: RequestStatus = RequestStatus.UNSYNCED, _action: EveryAction) => state
    }) as any,
    initialDataReducer: IReducer<State, EveryAction> = (state: State | undefined = undefined as unknown as State, action: EveryAction) => {
        if (action.type === 'LOAD_APP_INITIAL_DATA' && action.payload.status === RequestStatus.SUCCEEDED) {
            const response = JSON.parse(action.payload.response || '{}');
            let updatedState = state;

            for (const storeKey in response) {
                if (Object.prototype.hasOwnProperty.call(response, storeKey) && Object.prototype.hasOwnProperty.call(state, storeKey)) {
                    const
                        storePart: any = response[storeKey],
                        defaultStorePart: any = updatedState[storeKey];

                    for (const storeProperty in storePart) {
                        if (Object.prototype.hasOwnProperty.call(storePart, storeProperty) && Object.prototype.hasOwnProperty.call(defaultStorePart, storeProperty)) {
                            const
                                defaultValue = defaultStorePart[storeProperty],
                                wrappedValue = storePart[storeProperty];

                            if (wrappedValue) {
                                const
                                    { EntityType, Value } = wrappedValue,
                                    defaultEntity = (defaults as any)[EntityType],
                                    isArray = Array.isArray(Value);

                                let filledValue: any = Value;

                                if (defaultEntity) {
                                    if (isArray) {
                                        filledValue = filledValue.map((rawValue: any) => deepAssign(defaultEntity, {
                                            Custom: {
                                                status: RequestStatus.SUCCEEDED,
                                                internalID: 0
                                            },
                                            ...rawValue
                                        }));
                                    }
                                    else {
                                        if (EntityType === 'SessionInformation') {
                                            filledValue = deepAssign(filledValue, {
                                                FunctionalityAccesses: filledValue.FunctionalityAccesses.map((access: any) => deepAssign(defaults.UserFunctionalityAccessReport, {
                                                    Custom: {
                                                        status: RequestStatus.SUCCEEDED,
                                                        internalID: 0
                                                    },
                                                    ...access
                                                })),
                                                SpecialMenuItems: filledValue.SpecialMenuItems.map((item: any) => deepAssign(defaults.SpecialMenuItem, {
                                                    Custom: {
                                                        status: RequestStatus.SUCCEEDED,
                                                        internalID: 0
                                                    },
                                                    ...item
                                                }))
                                            });
                                        }

                                        filledValue = deepAssign(defaultEntity, {
                                            Custom: {
                                                status: RequestStatus.SUCCEEDED,
                                                internalID: 0
                                            },
                                            ...filledValue
                                        });
                                    }
                                }

                                const fakeAction = deepAssign(action, { payload: { response: filledValue } });

                                let defaultState = defaultValue;
                                if (!isArray) {
                                    if (Object.prototype.hasOwnProperty.call(defaultState, 'Data')) {
                                        defaultState = deepAssign(defaultState, { Data: filledValue });
                                    }
                                    else {
                                        defaultState = { ...defaultState, ...filledValue };
                                    }
                                }

                                updatedState = deepAssign(updatedState, {
                                    [storeKey]: {
                                        [storeProperty]: isArray ?
                                            entityLoadAllReducer(defaultValue, action.type)(defaultState, fakeAction) :
                                            networkActionReducer(defaultValue, action.type)(defaultState, fakeAction)
                                    }
                                });

                                if (storeKey === 'app' && (storeProperty === 'messages' || storeProperty === 'users')) {
                                    updatedState = deepAssign(updatedState, {
                                        [storeKey]: {
                                            [storeProperty]: {
                                                Custom: {
                                                    expires: new Date(+new Date() + SESSION_EXPIRATION_TIMEOUT)
                                                }
                                            }
                                        }
                                    });
                                }
                            }
                        }
                    }
                }
            }

            return updatedState;
        }

        return state;
    },
    freezeReducer: IReducer<State, EveryAction> = (state: State | undefined = undefined as unknown as State, _action: EveryAction) => deepFreeze(state);

let store: Store<State, EveryAction>,
    syncedHistory: History = history;
if (DEBUG) {
    syncedHistory = syncHistoryWithStore(history,
        store = createStore<State, EveryAction, {}, {}>(
            switchReducers(
                undefined as unknown as State,
                initialReducer,
                networkTrackingReducer,
                expirationReducer,
                scrollingReducer,
                mainReducer,
                initialDataReducer,
                freezeReducer
            ),
            applyMiddleware(
                thunk,
                routerMiddleware(history),
                require('redux-logger')({
                    actionTransformer: (action: any) => action.payload && action.payload.status ?
                        { ...action, type: `${action.type}/${action.payload.status}` } :
                        action,
                    // usually each of indicatorsData contains thousands of items and they slow down redux-logger, so it's easier to exclude them from the output
                    // if you really need it, just remove the field below; don't forget to revert it before you commit
                    // also number is fake, just something that should change when some indicatorData changes to force the line into diff output
                    stateTransformer: (state: any) => deepAssign(state, {
                        dashboards: {
                            indicatorsData: state.dashboards.indicatorsData.map((data: entities.PerformanceIndicatorDataReport) => ({
                                Data: `${data.Data.length} items, the changes are too big to include into logs. Check store.ts`
                            }))
                        }
                    }),
                    diff: true,
                    diffPredicate: (_state: any, action: any) => !action.type.startsWith('LOAD_ALL'),
                    collapsed: true,
                    logErrors: false
                }),
                ...Object.values(middlewares) as any[]
            )
        )
    );
}
else {
    syncedHistory = syncHistoryWithStore(history,
        store = createStore<State, EveryAction, {}, {}>(
            switchReducers(
                undefined as unknown as State,
                initialReducer,
                networkTrackingReducer,
                expirationReducer,
                scrollingReducer,
                mainReducer,
                initialDataReducer
            ),
            applyMiddleware(
                thunk,
                routerMiddleware(history),
                ...Object.values(middlewares) as any[]
            )
        )
    );
}


export { syncedHistory as history };
export default store;
